import { useEffect, useRef } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

interface MathDisplayProps {
    children: string;
    className?: string;
    fontSize?: string;
    displayMode?: boolean;
}

function unicodeToLatex(expr: string): string {
    let r = expr;
    r = r.replace(/⁻¹/g, '^{-1}');
    r = r.replace(/²/g, '^{2}'); r = r.replace(/³/g, '^{3}');
    r = r.replace(/⁴/g, '^{4}'); r = r.replace(/⁵/g, '^{5}');
    r = r.replace(/⁶/g, '^{6}'); r = r.replace(/⁸/g, '^{8}');
    r = r.replace(/⁹/g, '^{9}'); r = r.replace(/⁰/g, '^{0}');
    r = r.replace(/ⁿ/g, '^{n}'); r = r.replace(/ˣ/g, '^{x}');
    r = r.replace(/₁/g, '_{1}'); r = r.replace(/₂/g, '_{2}');
    r = r.replace(/₃/g, '_{3}'); r = r.replace(/ₙ/g, '_{n}');
    r = r.replace(/(\d?)√(\d+)/g, (_, c, n) => c ? `${c}\\sqrt{${n}}` : `\\sqrt{${n}}`);
    r = r.replace(/∛(\d+)/g, '\\sqrt[3]{$1}');
    r = r.replace(/−/g, '-'); r = r.replace(/×/g, '\\times ');
    r = r.replace(/÷/g, '\\div '); r = r.replace(/π/g, '\\pi ');
    r = r.replace(/≤/g, '\\leq '); r = r.replace(/≥/g, '\\geq ');
    r = r.replace(/±/g, '\\pm '); r = r.replace(/°/g, '^{\\circ}');
    return r;
}

function convertUnicodeMath(text: string): string {
    let r = text;
    r = r.replace(/\(([^)]+)\)\/\(([^)]+)\)/g, (_, n, d) =>
        `\\(\\frac{${unicodeToLatex(n)}}{${unicodeToLatex(d)}}\\)`);
    r = r.replace(/(\d+)\/\(([^)]+)\)/g, (_, n, d) =>
        `\\(\\frac{${n}}{${unicodeToLatex(d)}}\\)`);
    r = r.replace(/(\d+)\/√(\d+)/g, (_, n, d) =>
        `\\(\\frac{${n}}{\\sqrt{${d}}}\\)`);
    r = r.replace(/(^|[^a-zA-Z\d\\])(\d+)\/([a-zA-Z])(?![a-zA-Z\d])/g, (_, prev, n, d) =>
        `${prev}\\(\\frac{${n}}{${d}}\\)`);
    r = r.replace(/(^|[^a-zA-Z\\])([a-zA-Z])\/(\d+)(?![.\d])/g, (_, prev, n, d) =>
        `${prev}\\(\\frac{${n}}{${d}}\\)`);
    r = r.replace(/\(([^)]+)\)\/(\d+)/g, (_, n, d) =>
        `\\(\\frac{${unicodeToLatex(n)}}{${d}}\\)`);
    r = r.replace(/(^|[^a-zA-Z])([abcdfhknpqrstuvwxyz])\/([abcdfhknpqrstuvwxyz])(?![a-zA-Z])/g, (_, prev, n, d) =>
        `${prev}\\(\\frac{${n}}{${d}}\\)`);
    r = r.replace(/(\d?)√\(([^)]+)\)/g, (_, c, inner) =>
        c ? `\\(${c}\\sqrt{${unicodeToLatex(inner)}}\\)` : `\\(\\sqrt{${unicodeToLatex(inner)}}\\)`);
    r = r.replace(/(\d)√(\d+)/g, '\\($1\\sqrt{$2}\\)');
    r = r.replace(/√(\d+)/g, '\\(\\sqrt{$1}\\)');
    r = r.replace(/∛(\d+)/g, '\\(\\sqrt[3]{$1}\\)');
    r = r.replace(/\(([^)]+)\)([²³⁴⁵⁶⁸⁹⁰⁻¹ⁿˣʸ]+)/g, (_, inner, sup) =>
        `\\(\\left(${unicodeToLatex(inner)}\\right)${unicodeToLatex(sup)}\\)`);
    r = r.replace(/(^|[^a-zA-Z])(\d?)([a-zA-Z])([²³⁴⁵⁶⁸⁹⁰⁻¹ⁿˣʸ]+)(?![a-zA-Z])/g, (_, prev, d, v, sup) =>
        `${prev}\\(${d}${v}${unicodeToLatex(sup)}\\)`);
    r = r.replace(/(^|[^a-zA-Z\d.])(\d+)([²³⁴⁵⁶⁸⁹⁰⁻¹ⁿˣʸ]+)(?![a-zA-Z\d])/g, (_, prev, base, sup) =>
        `${prev}\\(${base}${unicodeToLatex(sup)}\\)`);
    return r;
}

function convertPlainMathToLatex(text: string): string {
    let r = text;
    r = r.replace(
        /(^|[^\\a-zA-Z])([a-zA-Z])\s*=\s*(-?\d+(?:\.\d+)?)(?![a-zA-Z\d\\])/g,
        (_, prev, v, n) => `${prev}\\(${v} = ${n}\\)`
    );

    // Fix typical standard form mistakes like '3.4 x 10^-{4}' or '34 x 10^{-5}' or '3.4 x 10^4'
    r = r.replace(/(-?\d+(?:\.\d+)?)\s*(?:x|X|\\times|\*)\s*10\s*\^?\s*\{?(-?)(\d+)\}?/g, 
        (_, base, minus, exp) => `\\(${base} \\times 10^{${minus}${exp}}\\)`);

    // Fix lone '10^-{4}' or '10^{-4}' (only if not preceded by something that looks like an exponent base multiplier)
    r = r.replace(/(^|[^0-9xX*×\\])\s*10\s*\^?\s*\{?(-?)(\d+)\}?/g, 
        (_, prev, minus, exp) => `${prev}\\(10^{${minus}${exp}}\\)`);

    return r;
}

function preprocessMathSymbols(text: string): string {
    const segments: string[] = [];
    const delimiterRegex = /\$\$([\s\S]*?)\$\$|\$([^\n$]+?)\$|\\\((.*?)\\\)|\\\[(.*?)\]/g;
    let lastIndex = 0;
    let match;
    while ((match = delimiterRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            let seg = text.substring(lastIndex, match.index);
            seg = convertUnicodeMath(seg);
            seg = convertPlainMathToLatex(seg);
            segments.push(seg);
        }
        segments.push(match[0]);
        lastIndex = delimiterRegex.lastIndex;
    }
    if (lastIndex < text.length) {
        let seg = text.substring(lastIndex);
        seg = convertUnicodeMath(seg);
        seg = convertPlainMathToLatex(seg);
        segments.push(seg);
    }
    return segments.join('');
}

function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

export default function MathDisplay({ children, className = '', fontSize, displayMode = false }: MathDisplayProps) {
    const ref = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (!ref.current) return;

        const preprocessed = preprocessMathSymbols(String(children || ""));

        const latexRegex = /\$\$([\s\S]*?)\$\$|\$([^\n$]+?)\$|\\\((.*?)\\\)|\\\[(.*?)\\\]|(\d+)\s+(\d+)\/(\d+)|(\d+)\/(\d+)/g;
        let html = '';
        let lastIndex = 0;
        let m;

        while ((m = latexRegex.exec(preprocessed)) !== null) {
            html += escapeHtml(preprocessed.substring(lastIndex, m.index));
            try {
                if (m[1] !== undefined) {
                    html += katex.renderToString(m[1], {
                        displayMode: true,
                        throwOnError: false,
                        output: 'html',
                    });
                } else if (m[2] !== undefined) {
                    html += katex.renderToString(m[2], {
                        displayMode: false,
                        throwOnError: false,
                        output: 'html',
                    });
                } else if (m[3] !== undefined) {
                    html += katex.renderToString(m[3], {
                        displayMode: displayMode,
                        throwOnError: false,
                        output: 'html',
                    });
                } else if (m[4] !== undefined) {
                    html += katex.renderToString(m[4], { displayMode: true, throwOnError: false, output: 'html' });
                } else if (m[5]) {
                    html += katex.renderToString(`${m[5]}\\frac{${m[6]}}{${m[7]}}`, {
                        displayMode: displayMode, throwOnError: false, output: 'html',
                    });
                } else if (m[8]) {
                    html += katex.renderToString(`\\frac{${m[8]}}{${m[9]}}`, {
                        displayMode: displayMode, throwOnError: false, output: 'html',
                    });
                }
            } catch {
                html += escapeHtml(m[0]);
            }
            lastIndex = latexRegex.lastIndex;
        }
        html += escapeHtml(preprocessed.substring(lastIndex));
        ref.current.innerHTML = html;
    }, [children, displayMode]);

    return (
        <span
            ref={ref}
            className={`math-serif whitespace-pre-wrap ${className ? ` ${className}` : ''}`}
            style={fontSize ? { fontSize } : undefined}
        />
    );
}
